import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClockCounterClockwise, CaretRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";

/**
 * PastWosAtUnitStrip — compact "history at this unit" card on every WO
 * page. Shows the last N (default 5) work orders at the same address+unit
 * so a tech / dispatcher can instantly see prior repair history without
 * navigating to the unit page.
 *
 * Queries `/properties/unit-detail?address=&unit=` (indexed) — returns the
 * full jobs_here list, we take the top N excluding the current WO.
 *
 * Renders nothing if there's no history — no chrome for empty state.
 */
export default function PastWosAtUnitStrip({ address, unit, excludeJobId, limit = 5 }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    if (!address || !unit) { setRows([]); return; }
    let alive = true;
    (async () => {
      try {
        const r = await api.get("/properties/unit-detail", { params: { address, unit } });
        const all = r.data?.jobs_here || [];
        const filt = all.filter(j => String(j.id) !== String(excludeJobId));
        if (alive) setRows(filt.slice(0, limit));
      } catch {
        if (alive) setRows([]);
      }
    })();
    return () => { alive = false; };
  }, [address, unit, excludeJobId, limit]);

  if (rows === null || rows.length === 0) return null;

  return (
    <div className="rounded-2xl bg-white border-2 border-slate-200 p-3 sm:p-4" data-testid="wo-past-strip">
      <div className="flex items-center gap-2 mb-2">
        <ClockCounterClockwise weight="fill" size={16} className="text-cyan-700"/>
        <div className="text-[10px] font-black uppercase tracking-widest text-slate-600">
          Past WOs at this unit ({rows.length})
        </div>
      </div>
      <div className="space-y-1">
        {rows.map(j => (
          <Link key={j.id} to={`/admin/jobs/${j.id}`}
            data-testid={`wo-past-row-${j.id}`}
            className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-slate-50 transition-colors group">
            <span className={`inline-flex items-center h-5 px-1.5 rounded text-[9px] font-black font-mono ${
              j.status === "completed" ? "bg-emerald-100 text-emerald-800" :
              j.status === "in_progress" ? "bg-cyan-100 text-cyan-800" :
              "bg-slate-100 text-slate-700"
            }`}>
              WO{j.work_order_number || j.id.slice(-4)}
            </span>
            <span className="text-sm text-slate-800 truncate flex-1 group-hover:text-cyan-700">
              {j.title || j.description?.slice(0, 60) || "Untitled"}
            </span>
            <span className="text-[10px] text-slate-500 whitespace-nowrap">
              {j.completed_at
                ? new Date(j.completed_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })
                : j.created_at
                ? new Date(j.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric" })
                : ""}
            </span>
            <CaretRight size={11} weight="bold" className="text-slate-300 group-hover:text-cyan-600 flex-shrink-0"/>
          </Link>
        ))}
      </div>
    </div>
  );
}
